import { errorMessage } from '@bixo/ui'

/**
 * The Android app's native side (apps/android, NativeBridge.java). The app
 * injects `window.BixoNative` into this origin only; in a normal browser it
 * is absent and every caller falls back to the web behaviour.
 */

interface NativePort {
  postMessage(message: string): void
  addEventListener(type: 'message', listener: (event: { data: unknown }) => void): void
}

export interface NativeInfo {
  platform: 'android'
  package: string
  version_name: string
  version_code: number
  bluetooth: boolean
  android: number
  /** Present from builds that can search for printers; older installs omit it. */
  features?: string[]
}

export interface NativePrinter {
  id: string
  name: string
  kind: 'bluetooth' | 'network'
  /** Bluetooth only: classic (serial) or Low Energy. */
  transport?: 'classic' | 'ble'
  paired?: boolean
  likely_printer?: boolean
}

interface Reply {
  id: number
  ok: boolean
  result?: unknown
  error?: string
}

/** An error the app itself reported: its message is meant for the user. */
export class NativeError extends Error {
  override name = 'NativeError'
}

/** The app's own message for its errors; the usual API handling for everything else. */
export function nativeErrorMessage(error: unknown, fallback: string): string {
  if (error instanceof NativeError) {
    return /^unknown method$/i.test(error.message)
      ? 'This copy of the app is out of date. Install the latest one from Settings → Business → Branding.'
      : error.message
  }
  return errorMessage(error, fallback)
}

const port = (): NativePort | null => (window as unknown as { BixoNative?: NativePort }).BixoNative ?? null

let nextId = 1
let listening = false
const pending = new Map<number, { resolve: (v: unknown) => void; reject: (e: Error) => void }>()

function listen(p: NativePort): void {
  if (listening) return
  listening = true
  p.addEventListener('message', (event) => {
    let reply: Reply
    try {
      reply = JSON.parse(String(event.data)) as Reply
    } catch {
      return
    }
    const call = pending.get(reply.id)
    if (!call) return
    pending.delete(reply.id)
    if (reply.ok) call.resolve(reply.result)
    else call.reject(new NativeError(reply.error ?? 'The app could not do that.'))
  })
}

/** Running inside the Bixo Android app? */
export const isNativeApp = (): boolean => port() !== null

export function nativeCall<T>(
  method: string,
  args: Record<string, unknown> = {},
  timeoutMs = 60_000,
): Promise<T> {
  const p = port()
  if (p === null) return Promise.reject(new NativeError('Only available in the Android app.'))
  listen(p)
  const id = nextId++
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => {
      pending.delete(id)
      reject(new NativeError('The app did not answer in time.'))
    }, timeoutMs)
    pending.set(id, {
      resolve: (v) => {
        clearTimeout(timer)
        resolve(v as T)
      },
      reject: (e) => {
        clearTimeout(timer)
        reject(e)
      },
    })
    p.postMessage(JSON.stringify({ id, method, args }))
  })
}

export async function blobToBase64(blob: Blob): Promise<string> {
  const buffer = new Uint8Array(await blob.arrayBuffer())
  let binary = ''
  for (let i = 0; i < buffer.length; i += 0x8000) {
    binary += String.fromCharCode(...buffer.subarray(i, i + 0x8000))
  }
  return btoa(binary)
}

export const native = {
  info: () => nativeCall<NativeInfo>('info'),
  /** Printers already paired with the phone. */
  printers: () => nativeCall<NativePrinter[]>('printers', {}, 30_000),
  /** Searches for nearby Bluetooth printers (classic and BLE), paired or not. */
  scanPrinters: (seconds = 10) =>
    nativeCall<NativePrinter[]>('scanPrinters', { seconds }, (seconds + 60) * 1000),
  printThermal: (html: string, width: 58 | 80, printer: string) =>
    nativeCall<boolean>('printThermal', { html, width, printer }, 90_000),
  /** Android's print service: any installed printer, or Save as PDF. */
  printHtml: (html: string, title: string, slipWidth?: 58 | 80) =>
    nativeCall<boolean>('printHtml', { html, title, ...(slipWidth ? { width: slipWidth } : {}) }),
  printPage: (title: string) => nativeCall<boolean>('printPage', { title }),
  saveFile: async (blob: Blob, name: string, open = false) =>
    nativeCall<string>('saveFile', {
      base64: await blobToBase64(blob),
      name,
      mime: blob.type || 'application/octet-stream',
      open,
    }),
  shareFile: async (
    blob: Blob,
    name: string,
    text: string,
    opts: { whatsapp?: boolean; phone?: string } = {},
  ) =>
    nativeCall<boolean>('shareFile', {
      base64: await blobToBase64(blob),
      name,
      mime: blob.type || 'application/octet-stream',
      text,
      whatsapp: opts.whatsapp ?? false,
      phone: opts.phone ?? '',
    }),
}
